import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import CollegeSearchCombobox, { CollegeLogo } from '../../components/CollegeSearchCombobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import '../../college-theme.css';

/* ─── Constants ──────────────────────────────────────────────────────── */

const PLAN_LABELS = {
  ED: 'Early Decision',
  ED_II: 'Early Decision II',
  EA: 'Early Action',
  REA: 'Restrictive Early Action',
  RD: 'Regular Decision',
  ROLLING: 'Rolling',
  UCAS: 'UCAS Main',
  OTHER: 'Other',
};

const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

// Click-to-retag cycle — UCAS and OTHER are fixed/manual-only, not cyclable.
const PLAN_CYCLE = ['ED', 'ED_II', 'EA', 'REA', 'RD', 'ROLLING'];

// Section order for the page (distinct from PLAN_CYCLE's retag order).
const SECTION_DEFS = [
  { plan: 'ED', title: 'Possible ED' },
  { plan: 'ED_II', title: 'Possible ED II' },
  { plan: 'REA', title: 'Possible REA' },
  { plan: 'EA', title: 'Possible EA' },
  { plan: 'RD', title: 'Regular Decision' },
  { plan: 'ROLLING', title: 'Rolling' },
  { plan: 'UCAS', title: 'UCAS' },
];

const STAGE_SEQUENCE = ['NOT_STARTED', 'ESSAYS', 'SUBMITTED', 'DECISION'];
const STAGE_LABELS = {
  NOT_STARTED: 'Not started',
  ESSAYS: 'Essays',
  SUBMITTED: 'Submitted',
  DECISION: 'Decision',
};

// Table column widths shared between the header row and every data row.
const GRID_COLUMNS = '32px minmax(160px, 1.6fr) 96px 130px 96px 150px 110px 90px';
const COLUMN_LABELS = ['', 'School', 'Plan', 'Deadline', 'Fit', 'Stage', 'Essays', 'Cost'];

const emptyIntlForm = () => ({
  collegeName: '', city: '', country: '', ibOffer: '', admitRate: '', costPerYear: '', enrollment: '', type: '',
});

/* ─── Helpers ────────────────────────────────────────────────────────── */

// Radix Dialog portals its content to document.body, which sits OUTSIDE the
// .college-scope div — so var(--college-*) custom properties and the
// .font-serif-editorial / .font-mono-editorial utility classes (both scoped
// under ".college-scope …") do not resolve inside it. The dialog below uses
// literal hex values and font-family strings instead of those tokens.
const HEX = {
  bg: '#F5F0E6', panel: '#FFFDF8', ink: '#16130D', inkSoft: '#4A453B',
  muted: '#7A7264', muted2: '#8A8375', border: '#DAD3C2', red: '#93321F',
};
const SERIF = "'Instrument Serif', Georgia, serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";

function nextJan1() {
  const year = new Date().getFullYear() + 1;
  return `${year}-01-01`;
}

function toDateInputValue(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function computeDays(deadlineStr) {
  const dt = new Date(deadlineStr);
  if (Number.isNaN(dt.getTime())) return 0;
  return Math.max(0, Math.ceil((dt - new Date()) / 86400000));
}

function sortByDeadline(list) {
  return [...list].sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
}

function formatCost(app) {
  const val = app.scorecardData?.tuitionOutOfState ?? app.manualStats?.costPerYear ?? null;
  if (val == null) return '—';
  return `$${Math.round(Number(val) / 1000)}k/yr`;
}

function fitSummary(apps) {
  const counts = { Reach: 0, Target: 0, Likely: 0 };
  apps.forEach((a) => { if (a.fit && counts[a.fit] !== undefined) counts[a.fit] += 1; });
  return ['Reach', 'Target', 'Likely']
    .filter((k) => counts[k] > 0)
    .map((k) => `${counts[k]} ${k.toLowerCase()}`)
    .join(', ');
}

/* ─── Column header (rendered once) ─────────────────────────────────── */

function ColumnHeader() {
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 16,
        padding: '0 4px 8px', borderBottom: '1px solid var(--college-ink)', marginBottom: 6,
      }}
    >
      {COLUMN_LABELS.map((h, i) => (
        <span key={i} className="label-editorial" style={{ fontSize: 10 }}>{h}</span>
      ))}
    </div>
  );
}

/* ─── Row ────────────────────────────────────────────────────────────── */

function Row({ app, essays, onCyclePlan, onDeadlineChange, onAdvanceStage }) {
  const shortPlan = PLAN_SHORT[app.plan] || app.plan;
  const filled = app.plan === 'ED';
  const interactive = app.plan !== 'UCAS' && app.plan !== 'OTHER';
  const stageIdx = Math.max(0, STAGE_SEQUENCE.indexOf(app.stage));
  const deadlineValue = toDateInputValue(app.deadline);
  const cost = formatCost(app);

  const essayTotal = essays ? essays.length : null;
  const essaySubmitted = essays ? essays.filter((e) => e.status === 'SUBMITTED').length : null;

  return (
    <div
      className="animate-fade-up"
      style={{
        display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 16, alignItems: 'center',
        padding: '14px 4px', borderBottom: '1px solid var(--college-border-soft)',
      }}
    >
      <CollegeLogo schoolUrl={app.schoolUrl} name={app.collegeName} size={28} />

      <div style={{ minWidth: 0 }}>
        <Link
          to={`/college/${app.id}`}
          className="font-serif-editorial"
          style={{
            fontSize: 18, color: 'var(--college-ink)', textDecoration: 'none', display: 'block',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {app.collegeName}
        </Link>
        {app.city && (
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>{app.city}</p>
        )}
      </div>

      {/* Plan chip */}
      <button
        type="button"
        disabled={!interactive}
        title={interactive ? 'Click to retag plan' : 'Fixed track'}
        onClick={() => interactive && onCyclePlan(app)}
        style={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
          padding: '5px 12px', borderRadius: 999, border: '1px solid var(--college-ink)',
          background: filled ? 'var(--college-ink)' : 'transparent',
          color: filled ? 'var(--college-panel)' : 'var(--college-ink)',
          cursor: interactive ? 'pointer' : 'default',
          justifySelf: 'start', width: 'fit-content', fontFamily: 'inherit',
        }}
      >
        {shortPlan}
      </button>

      {/* Deadline */}
      <div>
        <input
          type="date"
          value={deadlineValue}
          onChange={(e) => e.target.value && onDeadlineChange(app, e.target.value)}
          className="font-mono-editorial"
          style={{
            border: 'none', background: 'transparent', fontSize: 13, color: 'var(--college-ink)',
            cursor: 'pointer', padding: 0, width: '100%', outline: 'none',
          }}
        />
        <p className="font-mono-editorial" style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--college-red)' }}>
          {app.days}d out
        </p>
      </div>

      {/* Fit */}
      {app.fit ? (
        <span
          style={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '4px 12px', borderRadius: 999, background: app.fitBg, color: app.fitColor,
            width: 'fit-content',
          }}
        >
          {app.fit}
        </span>
      ) : (
        <span style={{ fontSize: 12, color: 'var(--college-muted-2)' }}>—</span>
      )}

      {/* Stage */}
      <div>
        <div
          onClick={() => onAdvanceStage(app)}
          title="Click to advance stage"
          style={{ display: 'flex', gap: 5, cursor: 'pointer', width: 'fit-content' }}
        >
          {STAGE_SEQUENCE.map((s, i) => (
            <span
              key={s}
              style={{
                width: 8, height: 8, borderRadius: '50%', border: '1px solid var(--college-ink)',
                background: i <= stageIdx ? 'var(--college-ink)' : 'transparent',
              }}
            />
          ))}
        </div>
        <p className="font-mono-editorial" style={{ margin: '5px 0 0', fontSize: 11, color: 'var(--college-muted)' }}>
          {STAGE_LABELS[app.stage] || app.stage}
        </p>
      </div>

      {/* Essays */}
      <div>
        {essayTotal == null ? (
          <span style={{ fontSize: 12, color: 'var(--college-muted-2)' }}>…</span>
        ) : essayTotal === 0 ? (
          <Link to={`/college/${app.id}`} style={{ fontSize: 12, color: 'var(--college-muted)', textDecoration: 'none' }}>
            No essays
          </Link>
        ) : (
          <>
            <p className="font-mono-editorial" style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--college-ink-soft)' }}>
              {essaySubmitted}/{essayTotal}
            </p>
            <div style={{ height: 4, width: '100%', background: 'var(--college-border)', borderRadius: 2, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%', width: `${essayTotal ? (essaySubmitted / essayTotal) * 100 : 0}%`,
                  background: 'var(--college-ink)',
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Cost */}
      <p className="font-mono-editorial" style={{ margin: 0, fontSize: 13, color: 'var(--college-ink-soft)' }}>{cost}</p>
    </div>
  );
}

/* ─── Section ────────────────────────────────────────────────────────── */

function Section({ title, rows, essaysByApp, onCyclePlan, onDeadlineChange, onAdvanceStage }) {
  const soonest = rows.reduce((min, r) => (min == null || r.days < min ? r.days : min), null);
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6, padding: '0 4px' }}>
        <h2 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 25, margin: 0 }}>
          {title}
        </h2>
        <p className="font-mono-editorial" style={{ margin: 0, fontSize: 12, color: 'var(--college-muted)' }}>
          {rows.length} school{rows.length === 1 ? '' : 's'} - next in {soonest}d
        </p>
      </div>
      <div>
        {rows.map((app) => (
          <Row
            key={app.id}
            app={app}
            essays={essaysByApp[app.id]}
            onCyclePlan={onCyclePlan}
            onDeadlineChange={onDeadlineChange}
            onAdvanceStage={onAdvanceStage}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── International-school dialog (fields only — no CSS-var/utility classes) ── */

function IntlSchoolDialog({ open, onOpenChange, form, setForm, onSubmit, saving }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputStyle = {
    width: '100%', border: `1px solid ${HEX.border}`, background: HEX.panel, color: HEX.ink,
    borderRadius: 4, padding: '8px 10px', fontSize: 13, fontFamily: "'Instrument Sans', sans-serif",
    outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', marginBottom: 5, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: HEX.muted2, fontFamily: "'Instrument Sans', sans-serif",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" style={{ background: HEX.panel, color: HEX.ink, border: `1px solid ${HEX.border}` }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: 24, color: HEX.ink }}>
            Add an international school
          </DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
          <div>
            <label style={labelStyle}>College / University *</label>
            <input style={inputStyle} value={form.collegeName} onChange={(e) => set('collegeName', e.target.value)} placeholder="e.g. University of Oxford" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Oxford" />
            </div>
            <div>
              <label style={labelStyle}>Country *</label>
              <input style={inputStyle} value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="UK" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>IB offer</label>
              <input style={inputStyle} type="number" value={form.ibOffer} onChange={(e) => set('ibOffer', e.target.value)} placeholder="38" />
            </div>
            <div>
              <label style={labelStyle}>Admit rate (%)</label>
              <input style={inputStyle} type="number" value={form.admitRate} onChange={(e) => set('admitRate', e.target.value)} placeholder="18" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Cost / year</label>
              <input style={inputStyle} type="number" value={form.costPerYear} onChange={(e) => set('costPerYear', e.target.value)} placeholder="35000" />
            </div>
            <div>
              <label style={labelStyle}>Enrollment</label>
              <input style={inputStyle} type="number" value={form.enrollment} onChange={(e) => set('enrollment', e.target.value)} placeholder="12000" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Type</label>
            <input style={inputStyle} value={form.type} onChange={(e) => set('type', e.target.value)} placeholder="Russell Group / public research university…" />
          </div>
          <p style={{ margin: 0, fontSize: 11, color: HEX.muted, fontFamily: "'Instrument Sans', sans-serif" }}>
            Its plan will be tagged UCAS (if country is UK) or Other, and its deadline will default to Jan 1 — both
            editable from My List afterward.
          </p>
        </div>
        <DialogFooter style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none', border: 'none', color: HEX.muted, fontSize: 13, cursor: 'pointer',
              fontFamily: "'Instrument Sans', sans-serif", padding: '8px 12px',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            style={{
              background: HEX.ink, color: HEX.panel, border: 'none', borderRadius: 4, fontSize: 13,
              fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1,
              fontFamily: "'Instrument Sans', sans-serif", padding: '9px 18px',
            }}
          >
            {saving ? 'Adding…' : 'Add school'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function CollegeListPage() {
  const { api } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [essaysByApp, setEssaysByApp] = useState({});
  const [searchKey, setSearchKey] = useState(0);
  const [intlOpen, setIntlOpen] = useState(false);
  const [intlForm, setIntlForm] = useState(emptyIntlForm());
  const [intlSaving, setIntlSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/college-applications')
      .then((res) => { if (alive) setApps(res.data.applications || []); })
      .catch(() => toast.error('Failed to load your list'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Essay counts, one call per school — fine for a handful of applications.
  // Keyed on the joined id list so plan/deadline/stage edits (which replace
  // application objects in state) don't trigger refetches.
  const appIdsKey = useMemo(() => apps.map((a) => a.id).join(','), [apps]);
  useEffect(() => {
    const ids = appIdsKey ? appIdsKey.split(',') : [];
    if (!ids.length) return;
    let alive = true;
    Promise.all(ids.map((id) =>
      api.get('/essays', { params: { collegeApplicationId: id } })
        .then((res) => [id, res.data.essays || []])
        .catch(() => [id, []])
    )).then((entries) => { if (alive) setEssaysByApp(Object.fromEntries(entries)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIdsKey, api]);

  const sections = useMemo(() => {
    const byPlan = {};
    apps.forEach((a) => {
      const key = SECTION_DEFS.some((s) => s.plan === a.plan) ? a.plan : 'OTHER';
      (byPlan[key] = byPlan[key] || []).push(a);
    });
    const ordered = SECTION_DEFS
      .map((s) => ({ ...s, rows: byPlan[s.plan] || [] }))
      .filter((s) => s.rows.length);
    if (byPlan.OTHER?.length) ordered.push({ plan: 'OTHER', title: 'Other', rows: byPlan.OTHER });
    return ordered;
  }, [apps]);

  const subtitle = useMemo(() => {
    const summary = fitSummary(apps);
    const base = `${apps.length} school${apps.length === 1 ? '' : 's'}`;
    return summary ? `${base} - ${summary}` : base;
  }, [apps]);

  /* ── mutations ── */

  const handleAddFromSearch = async (school) => {
    if (!school) return;
    try {
      const city = [school.city, school.state].filter(Boolean).join(', ');
      const res = await api.post('/college-applications', {
        collegeName: school.name,
        schoolUrl: school.schoolUrl || null,
        domain: school.domain || null,
        city: city || null,
        country: 'US',
        lat: school.lat ?? null,
        lng: school.lng ?? null,
        scorecardData: school,
        plan: 'RD',
        planLabel: PLAN_LABELS.RD,
        deadline: nextJan1(),
      });
      setApps((prev) => sortByDeadline([...prev, res.data.application]));
      toast.success(`${school.name} added to My List`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add school');
    } finally {
      setSearchKey((k) => k + 1); // remount combobox to clear the query
    }
  };

  const handleAddIntl = async () => {
    if (!intlForm.collegeName.trim()) return toast.error('College name is required');
    if (!intlForm.country.trim()) return toast.error('Country is required');
    const country = intlForm.country.trim();
    const plan = country.toUpperCase() === 'UK' ? 'UCAS' : 'OTHER';
    setIntlSaving(true);
    try {
      const res = await api.post('/college-applications', {
        collegeName: intlForm.collegeName.trim(),
        city: intlForm.city.trim() || null,
        country,
        plan,
        planLabel: PLAN_LABELS[plan],
        deadline: nextJan1(),
        manualStats: {
          ibOffer: intlForm.ibOffer ? Number(intlForm.ibOffer) : null,
          admitRate: intlForm.admitRate ? Number(intlForm.admitRate) / 100 : null,
          costPerYear: intlForm.costPerYear ? Number(intlForm.costPerYear) : null,
          enrollment: intlForm.enrollment ? Number(intlForm.enrollment) : null,
          type: intlForm.type.trim() || null,
        },
      });
      setApps((prev) => sortByDeadline([...prev, res.data.application]));
      toast.success(`${res.data.application.collegeName} added to My List`);
      setIntlOpen(false);
      setIntlForm(emptyIntlForm());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add school');
    } finally {
      setIntlSaving(false);
    }
  };

  const handleCyclePlan = async (app) => {
    const idx = PLAN_CYCLE.indexOf(app.plan);
    const nextPlan = PLAN_CYCLE[(idx + 1) % PLAN_CYCLE.length];
    const nextLabel = PLAN_LABELS[nextPlan];
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, plan: nextPlan, planLabel: nextLabel } : a)));
    try {
      const res = await api.put(`/college-applications/${app.id}`, { plan: nextPlan, planLabel: nextLabel });
      setApps((prev) => prev.map((a) => (a.id === app.id ? res.data.application : a)));
    } catch {
      setApps((prev) => prev.map((a) => (a.id === app.id ? app : a)));
      toast.error('Failed to update plan');
    }
  };

  const handleDeadlineChange = async (app, value) => {
    const prev = app;
    setApps((cur) => sortByDeadline(cur.map((a) => (a.id === app.id ? { ...a, deadline: value, days: computeDays(value) } : a))));
    try {
      const res = await api.put(`/college-applications/${app.id}`, { deadline: value });
      setApps((cur) => sortByDeadline(cur.map((a) => (a.id === app.id ? res.data.application : a))));
    } catch {
      setApps((cur) => sortByDeadline(cur.map((a) => (a.id === app.id ? prev : a))));
      toast.error('Failed to update deadline');
    }
  };

  const handleAdvanceStage = async (app) => {
    const idx = STAGE_SEQUENCE.indexOf(app.stage);
    const nextStage = STAGE_SEQUENCE[(idx + 1) % STAGE_SEQUENCE.length];
    setApps((prev) => prev.map((a) => (a.id === app.id ? { ...a, stage: nextStage } : a)));
    try {
      const res = await api.put(`/college-applications/${app.id}`, { stage: nextStage });
      setApps((prev) => prev.map((a) => (a.id === app.id ? res.data.application : a)));
    } catch {
      setApps((prev) => prev.map((a) => (a.id === app.id ? app : a)));
      toast.error('Failed to update stage');
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap',
          gap: 24, borderBottom: '1px solid var(--college-border)', paddingBottom: 28, marginBottom: 36,
        }}
      >
        <div>
          <h1 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 52, lineHeight: 1, margin: '0 0 10px' }}>
            My List.
          </h1>
          <p className="font-mono-editorial" style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            {subtitle}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ width: 320 }}>
            <p className="label-editorial" style={{ marginBottom: 6, textAlign: 'right' }}>
              Add a school — search Scorecard…
            </p>
            <div style={{ border: '1px solid var(--college-ink)', background: 'var(--college-panel)', padding: 2 }}>
              <CollegeSearchCombobox key={searchKey} value="" schoolUrl={null} onSelect={handleAddFromSearch} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIntlOpen(true)}
            className="font-mono-editorial"
            style={{
              background: 'none', border: 'none', color: 'var(--college-red)', fontSize: 12,
              cursor: 'pointer', textDecoration: 'underline', padding: 0,
            }}
          >
            + add an international school
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="font-mono-editorial" style={{ padding: '48px 0', textAlign: 'center', color: 'var(--college-muted)', fontSize: 13 }}>
          Loading your list…
        </div>
      ) : apps.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center', border: '1px dashed var(--college-border)' }}>
          <p className="font-serif-editorial" style={{ fontStyle: 'italic', fontSize: 24, margin: '0 0 8px' }}>
            No schools yet.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            Search above to add your first school, or use "add an international school".
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 760 }}>
            <ColumnHeader />
            {sections.map((section) => (
              <Section
                key={section.plan}
                title={section.title}
                rows={section.rows}
                essaysByApp={essaysByApp}
                onCyclePlan={handleCyclePlan}
                onDeadlineChange={handleDeadlineChange}
                onAdvanceStage={handleAdvanceStage}
              />
            ))}
          </div>
        </div>
      )}

      {/* Footer caption */}
      <p
        className="font-mono-editorial"
        style={{ marginTop: 48, fontSize: 11, color: 'var(--college-muted-2)', borderTop: '1px solid var(--college-border)', paddingTop: 16 }}
      >
        US stats: College Scorecard (live API) - international entries: manual data - fit recomputed from your profile - plan tags cycle ED to ED II to EA to REA to RD to Rolling
      </p>

      <IntlSchoolDialog
        open={intlOpen}
        onOpenChange={(o) => { setIntlOpen(o); if (!o) setIntlForm(emptyIntlForm()); }}
        form={intlForm}
        setForm={setIntlForm}
        onSubmit={handleAddIntl}
        saving={intlSaving}
      />
    </div>
  );
}
